import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ToastContainer } from 'react-toastify';
import { Toaster } from 'sonner';
import 'react-toastify/dist/ReactToastify.css';

import DocumentHead from './components/common/DocumentHead';
import PageLoader from './components/common/PageLoader';
import Navbar from './components/Navbar';
import SingleLegacyRedirect from './components/SingleLegacyRedirect';
import LandingPage from './components/LandingPage';
import PodcastCampaignRedirect from './components/PodcastCampaignRedirect';
import AppDownloadPromo from './components/common/AppDownloadPromo';
import ProtectedRoute from './components/ProtectedRoute';
import SubscriptionGate from './components/subscription/SubscriptionGate';

import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { NotificationProvider } from './context/NotificationContext';
import { useInactivityLogout } from './hooks/useInactivityLogout';
import { useMatchMedia } from './hooks/useMatchMedia';
import { getAdShortLinkSlugFromPath } from './utils/adShortLinkRouting';

const Login = lazy(() => import('./components/auth/Login'));
const Register = lazy(() => import('./components/auth/Register'));
const AuthTypeChooser = lazy(() => import('./components/auth/AuthTypeChooser'));
const GoogleCompleteProfile = lazy(() => import('./components/auth/GoogleCompleteProfile'));
const ForgotPassword = lazy(() => import('./components/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/auth/ResetPassword'));
const VerifyEmail = lazy(() => import('./components/auth/VerifyEmail'));
const ShadchanDashboard = lazy(() => import('./components/shadchan/Dashboard'));
const ShadchanSettings = lazy(() => import('./components/shadchan/Settings'));
const SingleDashboard = lazy(() => import('./components/single/Dashboard'));
const ChatPage = lazy(() => import('./components/chat/ChatPage'));
const ParentDashboard = lazy(() => import('./components/parent/Dashboard'));
const MyResumeComingSoon = lazy(() => import('./components/resumes/MyResumeComingSoon'));
const GettingStarted = lazy(() => import('./components/pages/GettingStarted'));
const About = lazy(() => import('./components/pages/About'));
const FAQ = lazy(() => import('./components/pages/FAQ'));
const MobileApp = lazy(() => import('./components/pages/MobileApp'));
const TermsOfUse = lazy(() => import('./components/pages/TermsOfUse'));
const PrivacyPolicy = lazy(() => import('./components/pages/PrivacyPolicy'));
const ContactSupport = lazy(() => import('./components/pages/ContactSupport'));
const RequestAccountDeletion = lazy(() => import('./components/pages/RequestAccountDeletion'));
const ChildSafetyCsae = lazy(() => import('./components/pages/ChildSafetyCsae'));
const SubscriptionPage = lazy(() => import('./components/subscription/SubscriptionPage'));
const SubscriptionSuccess = lazy(() => import('./components/subscription/SubscriptionSuccess'));

const HomePage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (isAuthenticated() && user) {
      if (user.role === 'shadchan') navigate('/shadchan/dashboard', { replace: true });
      else if (user.role === 'single') navigate('/single/dashboard', { replace: true });
      else if (user.role === 'parent') navigate('/parent/dashboard', { replace: true });
      else navigate('/login', { replace: true });
    }
  }, [user, isAuthenticated, navigate]);

  if (!isAuthenticated()) {
    return <LandingPage />;
  }

  return null;
};

const InactivityHandler = () => {
  useInactivityLogout();
  return null;
};

const ContainerLayout = ({ children }) => (
  <main className="container container-narrow py-4 page-enter">
    <Suspense fallback={<PageLoader />}>
      {children}
    </Suspense>
  </main>
);

function AppRoutes() {
  const location = useLocation();
  const path = (location.pathname || '/').replace(/\/+$/, '') || '/';
  const adShortSlug = getAdShortLinkSlugFromPath(path);
  const isBareShellPath = path === '/' || adShortSlug != null;
  const isPodcastShortRedirect = adShortSlug != null;
  const isMobileToastLayout = useMatchMedia('(max-width: 767px)');

  return (
    <>
      <DocumentHead />
      <div className="app-shell">
        <InactivityHandler />
        {!isPodcastShortRedirect && <Navbar />}
        {!isPodcastShortRedirect && <AppDownloadPromo />}

        <Toaster
          position={isMobileToastLayout ? 'bottom-center' : 'top-right'}
          offset={
            isMobileToastLayout
              ? { bottom: 'max(5.5rem, calc(1rem + env(safe-area-inset-bottom, 0px)))' }
              : undefined
          }
          toastOptions={{
            style: {
              background: 'white',
              border: '1px solid hsl(214.3 31.8% 91.4%)',
              borderRadius: '0.75rem',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
              padding: '16px',
            },
          }}
          richColors
          closeButton
        />

        {isBareShellPath ? (
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/:adSlug" element={<PodcastCampaignRedirect />} />
          </Routes>
        ) : (
          <ContainerLayout>
            <Routes>
              <Route path="/login" element={<AuthTypeChooser mode="login" />} />
              <Route path="/login/shadchan" element={<Login role="shadchan" />} />
              <Route path="/login/single" element={<Login role="single" />} />
              <Route path="/login/parent" element={<Login role="parent" />} />

              <Route path="/register" element={<AuthTypeChooser mode="register" />} />
              <Route path="/register/shadchan" element={<Register role="shadchan" />} />
              <Route path="/register/single" element={<Register role="single" />} />
              <Route path="/register/parent" element={<Register role="parent" />} />

              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/auth/google-complete" element={<GoogleCompleteProfile />} />

              <Route path="/getting-started" element={<GettingStarted />} />
              <Route path="/about" element={<About />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/mobile-app" element={<MobileApp />} />
              <Route path="/terms" element={<TermsOfUse />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/contact" element={<ContactSupport />} />
              <Route path="/delete-account" element={<RequestAccountDeletion />} />
              <Route path="/data-deletion" element={<RequestAccountDeletion />} />
              <Route path="/child-safety" element={<ChildSafetyCsae />} />
              <Route path="/csae-standards" element={<ChildSafetyCsae />} />

              <Route
                path="/shadchan/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['shadchan']}>
                    <ShadchanDashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/shadchan/settings"
                element={
                  <ProtectedRoute allowedRoles={['shadchan']}>
                    <ShadchanSettings />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/single/profile"
                element={
                  <ProtectedRoute allowedRoles={['single']}>
                    <SingleLegacyRedirect />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/single/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['single']}>
                    <SubscriptionGate>
                      <SingleDashboard />
                    </SubscriptionGate>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/messages"
                element={
                  <ProtectedRoute allowedRoles={['shadchan', 'single', 'parent']}>
                    <ChatPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/parent/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['parent']}>
                    <SubscriptionGate>
                      <ParentDashboard />
                    </SubscriptionGate>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/my-resumes"
                element={
                  <ProtectedRoute allowedRoles={['single', 'parent']}>
                    <SubscriptionGate>
                      <MyResumeComingSoon />
                    </SubscriptionGate>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/subscription"
                element={
                  <ProtectedRoute allowedRoles={['single', 'parent']}>
                    <SubscriptionPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription/success"
                element={
                  <ProtectedRoute allowedRoles={['single', 'parent']}>
                    <SubscriptionSuccess />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription/cancel"
                element={
                  <div className="max-w-lg mx-auto mt-8">
                    <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center shadow-sm">
                      <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-4">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Checkout Canceled</h4>
                      <p className="text-gray-500 mb-6">Your subscription checkout was canceled. No charges were made.</p>
                      <a href="/subscription" className="btn btn-primary no-underline">Return to Subscription Page</a>
                    </div>
                  </div>
                }
              />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ContainerLayout>
        )}

        <ToastContainer
          position={isMobileToastLayout ? 'bottom-center' : 'top-right'}
          autoClose={4000}
          hideProgressBar
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          toastStyle={{
            borderRadius: '0.75rem',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
            border: '1px solid hsl(214.3 31.8% 91.4%)',
          }}
        />
      </div>
    </>
  );
}

function App() {
  const googleClientId =
    process.env.REACT_APP_GOOGLE_CLIENT_ID ||
    '902370383615-0sbd4bptmfkrdu7pklqd5vmq3mrc3fie.apps.googleusercontent.com';

  return (
    <LanguageProvider>
      <AuthProvider>
        <NotificationProvider>
          <GoogleOAuthProvider clientId={googleClientId}>
            <Router>
              <AppRoutes />
            </Router>
          </GoogleOAuthProvider>
        </NotificationProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
